import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Menu, LogOut, User, LayoutDashboard, ChevronDown, Users, Compass, Briefcase, ShieldCheck, Lock } from 'lucide-react';
import WorkspaceLayout from '@/components/WorkspaceLayout';
import { MENTOR_NAV, PARENT_NAV, SELF_WRAPPED_ROUTES, SHARED_DETAIL_PREFIXES } from '@/lib/workspaceNav';

const publicLinks = [
  { label: 'Home', path: '/' },
  { label: 'Explore Careers', path: '/careers' },
  { label: 'For Schools', path: '/for-schools' },
  { label: 'Our Story', path: '/about' },
];

function getDashboardPath(role) {
  if (role === 'admin') return '/admin';
  if (role === 'coordinator') return '/coordinator';
  if (role === 'parent') return '/parent';
  if (role === 'partner') return '/partner';
  return '/mentor';
}

function getDashboardLabel(role) {
  if (role === 'admin') return 'Admin Dashboard';
  if (role === 'coordinator') return 'Career Coordinator Dashboard';
  if (role === 'parent') return 'Parent Dashboard';
  if (role === 'partner') return 'Partner Dashboard';
  return 'Mentor Dashboard';
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  // Parent/Mentor workspace: pages that wrap themselves render bare, shared detail
  // pages are wrapped here so the modern header/nav is never lost.
  if (SELF_WRAPPED_ROUTES.includes(location.pathname)
    || (location.pathname.startsWith('/partner/') && !location.pathname.startsWith('/partner/invite'))) {
    return <>{children}</>;
  }
  if (user?.role === 'parent' && SHARED_DETAIL_PREFIXES.some(p => location.pathname.startsWith(p))) {
    const mentorContext = Boolean(user?.is_mentor)
      && localStorage.getItem('cpov_last_dashboard') !== 'parent'
      && !location.pathname.startsWith('/parent/children/')
      && !location.pathname.startsWith('/parent/safety-concerns');
    return (
      <WorkspaceLayout
        nav={mentorContext ? MENTOR_NAV : PARENT_NAV}
        activeRole={mentorContext ? 'mentor' : 'parent'}
        testId="workspace-detail-page"
      >
        {children}
      </WorkspaceLayout>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F6F8]">
      {/* Header */}
      <header
        className={`sticky top-0 z-50 bg-white/85 backdrop-blur-xl${
          location.pathname.startsWith('/coordinator') ? ' hidden lg:block' : ''}`}
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)' }}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-1.5" data-testid="logo-link">
              <span className="text-[18px] sm:text-[23px] font-bold text-[#1F2937] font-['Outfit'] tracking-tight flex items-center">
                Careers<span className="inline-flex items-center ml-0.5"><span className="bg-[#F97D6A] text-white text-[10px] sm:text-[14px] font-extrabold px-1.5 sm:px-2 py-[2px] sm:py-[3px] rounded-[4px] sm:rounded-[5px] leading-none tracking-wide">POV</span></span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
              {publicLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive(link.path)
                      ? 'text-[#F97D6A] bg-[#FFF0ED]'
                      : 'text-[#4B5563] hover:text-[#1F2937] hover:bg-gray-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Desktop Auth */}
            <div className="hidden md:flex items-center gap-3" data-testid="desktop-auth">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex items-center gap-2 h-9 px-3"
                      data-testid="user-menu-trigger"
                    >
                      <div className="w-7 h-7 rounded-full bg-[#F97D6A] flex items-center justify-center">
                        <span className="text-white text-xs font-semibold">
                          {user.full_name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-[#1F2937] max-w-[120px] truncate">
                        {user.full_name || user.email}
                      </span>
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-2">
                      <p className="text-sm font-semibold text-[#1F2937]">{user.full_name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                      <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider text-[#F97D6A] bg-[#FFF0ED] px-2 py-0.5 rounded-full">
                        {user.role === 'parent' && user.is_mentor && user.mentor_approval_status === 'approved'
                          ? 'Parent · Mentor'
                          : user.role === 'coordinator' ? 'Career Coordinator' : user.role}
                      </span>
                    </div>
                    <DropdownMenuSeparator />
                    {user.role !== 'parent' && (
                      <DropdownMenuItem
                        onClick={() => navigate(getDashboardPath(user.role))}
                        data-testid="menu-dashboard"
                      >
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        {getDashboardLabel(user.role)}
                      </DropdownMenuItem>
                    )}
                    {user.role === 'parent' && (
                      <DropdownMenuItem
                        onClick={() => { localStorage.setItem('cpov_last_dashboard', 'parent'); navigate('/parent'); }}
                        data-testid="menu-parent-dashboard"
                      >
                        <Users className="mr-2 h-4 w-4" />
                        Parent Dashboard
                      </DropdownMenuItem>
                    )}
                    {user.role === 'parent' && user.is_mentor && (
                      <DropdownMenuItem
                        onClick={() => { localStorage.setItem('cpov_last_dashboard', 'mentor'); navigate('/mentor'); }}
                        data-testid="menu-mentor-dashboard"
                      >
                        <Briefcase className="mr-2 h-4 w-4" />
                        Mentor Dashboard
                      </DropdownMenuItem>
                    )}
                    {user.role === 'parent' && !user.is_mentor && (
                      <DropdownMenuItem
                        onClick={() => navigate('/mentor/onboarding')}
                        data-testid="menu-become-mentor"
                      >
                        <Briefcase className="mr-2 h-4 w-4" />
                        Become a Mentor
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => navigate('/careers')}
                      data-testid="menu-explore-careers"
                    >
                      <Compass className="mr-2 h-4 w-4" />
                      Explore Careers
                    </DropdownMenuItem>
                    {user.role === 'parent' && (
                      <DropdownMenuItem
                        onClick={() => navigate('/parent/safety-concerns')}
                        data-testid="menu-safety-concerns"
                      >
                        <ShieldCheck className="mr-2 h-4 w-4" />
                        Safety Concerns
                      </DropdownMenuItem>
                    )}
                    {user.role === 'parent' && (
                      <DropdownMenuItem
                        onClick={() => navigate('/parent/privacy-permissions')}
                        data-testid="menu-privacy-permissions"
                      >
                        <Lock className="mr-2 h-4 w-4" />
                        Privacy &amp; Permissions
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="mr-2 h-4 w-4" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link to="/login">
                    <Button
                      variant="ghost"
                      className="text-sm text-[#4B5563] hover:text-[#1F2937]"
                      data-testid="nav-login-btn"
                    >
                      Log in
                    </Button>
                  </Link>
                  <Link to="/school-registration">
                    <Button
                      className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl text-sm px-5"
                      data-testid="nav-register-btn"
                    >
                      Register School
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Hamburger */}
            <div className="md:hidden">
              <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                    <Menu className="h-5 w-5 text-[#1F2937]" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[280px] bg-white">
                  <SheetHeader>
                    <SheetTitle className="text-left font-['Outfit'] text-[#1F2937] flex items-center gap-1">
                      Careers<span className="bg-[#F97D6A] text-white text-[10px] font-extrabold px-1.5 py-0.5 rounded-[4px] leading-none tracking-wide">POV</span>
                    </SheetTitle>
                  </SheetHeader>
                  <nav className="flex flex-col gap-1 mt-6" data-testid="mobile-nav">
                    {publicLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setSheetOpen(false)}
                        data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`px-4 py-3 text-sm font-medium rounded-xl transition-colors ${
                          isActive(link.path)
                            ? 'text-[#F97D6A] bg-[#FFF0ED]'
                            : 'text-[#4B5563] hover:bg-gray-50'
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}

                    {user && (
                      <Link
                        to={getDashboardPath(user.role)}
                        onClick={() => setSheetOpen(false)}
                        data-testid="mobile-nav-dashboard"
                        className={`px-4 py-3 text-sm font-medium rounded-xl transition-colors flex items-center gap-2 ${
                          isActive(getDashboardPath(user.role))
                            ? 'text-[#F97D6A] bg-[#FFF0ED]'
                            : 'text-[#4B5563] hover:bg-gray-50'
                        }`}
                      >
                        <LayoutDashboard className="h-4 w-4" />
                        {getDashboardLabel(user.role)}
                      </Link>
                    )}

                    <div className="border-t border-gray-100 mt-3 pt-3">
                      {user ? (
                        <>
                          <div className="px-4 py-2 mb-2">
                            <p className="text-sm font-semibold text-[#1F2937]">{user.full_name}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                          {user.role === 'parent' && (
                            <Link
                              to="/parent/safety-concerns"
                              onClick={() => setSheetOpen(false)}
                              data-testid="mobile-safety-concerns"
                              className="w-full px-4 py-3 text-sm font-medium text-[#4B5563] hover:bg-gray-50 rounded-xl transition-colors flex items-center gap-2"
                            >
                              <ShieldCheck className="h-4 w-4" />
                              Safety Concerns
                            </Link>
                          )}
                          {user.role === 'parent' && (
                            <Link
                              to="/parent/privacy-permissions"
                              onClick={() => setSheetOpen(false)}
                              data-testid="mobile-privacy-permissions"
                              className="w-full px-4 py-3 text-sm font-medium text-[#4B5563] hover:bg-gray-50 rounded-xl transition-colors flex items-center gap-2"
                            >
                              <Lock className="h-4 w-4" />
                              Privacy &amp; Permissions
                            </Link>
                          )}
                          <button
                            onClick={() => { handleLogout(); setSheetOpen(false); }}
                            className="w-full px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors text-left flex items-center gap-2"
                            data-testid="mobile-logout-btn"
                          >
                            <LogOut className="h-4 w-4" />
                            Log out
                          </button>
                        </>
                      ) : (
                        <div className="flex flex-col gap-2 px-4">
                          <Link to="/login" onClick={() => setSheetOpen(false)}>
                            <Button
                              variant="outline"
                              className="w-full rounded-xl"
                              data-testid="mobile-login-btn"
                            >
                              Log in
                            </Button>
                          </Link>
                          <Link to="/school-registration" onClick={() => setSheetOpen(false)}>
                            <Button
                              className="w-full bg-[#F97D6A] hover:bg-[#F06651] rounded-xl text-white"
                              data-testid="mobile-register-btn"
                            >
                              Register School
                            </Button>
                          </Link>
                        </div>
                      )}
                    </div>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-7" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-3">
              <Link to="/" className="text-sm font-bold text-[#1F2937] font-['Outfit'] flex items-center">
                Careers<span className="bg-[#F97D6A] text-white text-[8px] font-extrabold px-1 py-0.5 rounded-[3px] leading-none tracking-wide ml-0.5">POV</span>
              </Link>
              <span className="hidden sm:inline text-xs text-[#D1D5DB]">&middot;</span>
              <p className="text-xs text-[#9CA3AF]" data-testid="footer-tagline">Experience Before You Choose.</p>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/terms" className="text-xs text-[#9CA3AF] hover:text-[#6B7280] transition-colors" data-testid="footer-terms-link">Terms of Use</Link>
              <span className="text-xs text-[#D1D5DB]">&middot;</span>
              <Link to="/privacy" className="text-xs text-[#9CA3AF] hover:text-[#6B7280] transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
              <span className="text-xs text-[#D1D5DB]">&middot;</span>
              <Link to="/student-safety" className="text-xs text-[#9CA3AF] hover:text-[#6B7280] transition-colors" data-testid="footer-safety-link">Student Safety</Link>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-[11px] text-[#9CA3AF]" data-testid="footer-copyright">&copy; 2026 CareersPOV. All rights reserved.</p>
            <a
              href="https://www.gigglygains.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-[#9CA3AF] hover:text-[#F97D6A] transition-colors"
              data-testid="footer-gigglygains-link"
            >
              A Giggly Gains venture
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
